import React from 'react';
import RouteLinkList from './RouteLinkList';
import SeoLayout from './SeoLayout';
import SiteFooter from './SiteFooter';
import {
  getAgencyDisplayName,
  getRouteAreaNames,
  getRouteLabel,
  slugify,
  titleizeSlug,
} from './routeUtils';

export function AreaPage({ areaSlug, schedules, loadingSchedules }) {
  const areaName = titleizeSlug(areaSlug);
  const routes = schedules
    .filter((schedule) =>
      getRouteAreaNames(schedule).some((name) => slugify(name) === areaSlug)
    )
    .sort((first, second) => getRouteLabel(first).localeCompare(getRouteLabel(second)));
  const operatorGroups = [...new Set(routes.map((route) => route.agency))]
    .map((agency) => ({ agency, routes: routes.filter((route) => route.agency === agency) }));
  const pageHeading = areaSlug === 'cape-town'
    ? 'Cape Town Bus Routes & Times'
    : `${areaName} Bus Times & Timetables`;

  return (
    <main className="info-page">
      <SeoLayout>
        <section className="info-panel seo-content-panel">
          <p className="info-eyebrow">Cape Town bus area</p>
          <h1>{pageHeading}</h1>
          <p>
            {routes.length} bus routes serve {areaName} on {[...new Set(routes.map((route) => getAgencyDisplayName(route.agency)))].join(' and ') || 'Fika'}.
            Select a route to view its timetable, stops, directions, and service days.
          </p>
          <p>
            Area pages are built from route names and direction labels in the timetable data. A
            route may appear here because it starts in {areaName}, ends there, or links commuters
            to the area.
          </p>
          <h2>Timetable notes</h2>
          <p>
            Fika is independent from transport operators. Use these area links for planning and
            route discovery, then confirm urgent service changes through Golden Arrow or MyCiTi
            before travelling.
          </p>
          <h2>Routes serving {areaName}</h2>
          {loadingSchedules ? (
            <p>Loading routes...</p>
          ) : (
            operatorGroups.length ? operatorGroups.map((group) => (
              <section key={group.agency} className="seo-route-group">
                <h3>{getAgencyDisplayName(group.agency)}</h3>
                <RouteLinkList routes={group.routes} emptyMessage="" selectionSource="area_page" />
              </section>
            )) : <p>No route matches are loaded for this area yet. Use search to find a route.</p>
          )}
        </section>
      </SeoLayout>
      <SiteFooter />
    </main>
  );
}

export function AreasIndexPage({ schedules, loadingSchedules }) {
  const areas = [...new Set(schedules.flatMap(getRouteAreaNames))]
    .sort((first, second) => first.localeCompare(second));

  return (
    <main className="info-page">
      <SeoLayout>
        <section className="info-panel seo-content-panel">
          <p className="info-eyebrow">Cape Town bus areas</p>
          <h1>Cape Town bus areas and stops</h1>
          <p>
            Browse Golden Arrow and MyCiTi timetable pages by Cape Town area, stop, and route coverage.
          </p>
          <p>
            The area index groups loaded timetable data into place-based pages so commuters can
            discover routes by suburb, interchange, terminal, or corridor instead of only by route
            number.
          </p>
          <h2>How areas are grouped</h2>
          <p>
            Areas are generated from route names and direction names. Some names may
            represent a terminal, station, or corridor rather than a municipal suburb boundary.
          </p>
          <h2>Areas and stops</h2>
          {loadingSchedules ? (
            <p>Loading areas...</p>
          ) : (
            <div className="seo-area-links">
              {areas.map((areaName) => (
                <a key={areaName} href={`/areas/${slugify(areaName)}`}>
                  {titleizeSlug(slugify(areaName))}
                </a>
              ))}
            </div>
          )}
        </section>
      </SeoLayout>
      <SiteFooter />
    </main>
  );
}
